import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Stethoscope } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { organizationSchema, physicianSchema, BRAND, OG_IMAGES, SITE_URL} from "@/lib/seo";
import { heroLeft, heroRight } from "./_partials/animationVariants";

const HEADSHOT =
  "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/rmekuye4_Mike%20Ellis.png";

const BIO = [
  "Dr. Michael Ellis, DO, is a licensed physician clinically trained in internal medicine. He completed his undergraduate studies at Penn State (Kinesiology), earned his medical degree at the Virginia College of Osteopathic Medicine, and finished his internal medicine residency at Saint Michael's Medical Center in Newark, NJ.",
  "After years on the front lines as a hospitalist — including Hackensack Meridian Riverview Medical Center, Monument Health, and Sanford Health Systems — he shifted his focus to wellness, longevity, and aesthetic medicine.",
  "He also serves as Chief Medical Officer of NFL Alumni Performance Labs (NFLAPL), Lead Investigator at Evolutionary Biologics, and CEO of Evoú Body Contouring.",
  "As Medical Director of Integrated Wellness Medical Center, Dr. Ellis oversees clinical protocols, licensed telemedicine care, and the safe delivery of every medical recommendation made by the Integrated Clinical Intelligence platform.",
];

export default function MedicalDirector() {
  useDocumentMeta({
    title: `${BRAND.medicalDirector.name} — Medical Director | Integrated Clinical Intelligence`,
    description:
      "Meet the Medical Director of Integrated Wellness Medical Center. Board-certified physician leading clinical protocols, hormone optimization, and licensed telemedicine care.",
    image: OG_IMAGES.medicalDirector,
    url: `${SITE_URL}/medical-director`,
    schema: [organizationSchema(), physicianSchema()],
  });

  return (
    <div data-testid="medical-director-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-28 md:pt-32 pb-24">
        <section className="max-w-5xl mx-auto px-4 md:px-8">
          <Link
            to="/medical-care"
            data-testid="back-to-medical-care-link"
            className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.32em] text-accent hover:text-primary transition-colors"
          >
            ← Back to Medical Care
          </Link>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
            <motion.div {...heroRight} className="md:col-span-4">
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border-2 border-primary/25 bg-card shadow-md">
                <img
                  src={HEADSHOT}
                  alt={`${BRAND.medicalDirector.name} — Medical Director at Integrated Wellness Medical Center`}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <div className="mt-5 flex items-center gap-2 font-mono text-[0.6rem] uppercase tracking-[0.22em] text-accent">
                <ShieldCheck size={14} className="text-accent" />
                Medical Director · Licensed Telemedicine
              </div>
            </motion.div>

            <motion.div {...heroLeft} className="md:col-span-8">
              <div className="iw-overline mb-4">Meet the Medical Director
              </div>
              <h1 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-[clamp(2.25rem,5vw,4rem)]">
                {BRAND.medicalDirector.name}
              </h1>
              <p className="mt-3 text-base md:text-lg italic text-foreground/55">
                Internal Medicine · Wellness &amp; Aesthetics · Medical Director, IWMC
              </p>

              <div className="mt-7 space-y-5">
                {BIO.map((para) => (
                  <p
                    key={para.slice(0, 40)}
                    className="text-base md:text-lg text-foreground/80 leading-relaxed"
                  >
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/medical-care"
                  data-testid="explore-medical-care-cta"
                  className="iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]"
                >
                  <Stethoscope size={16} aria-hidden /> Explore Medical Care
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
